import { CONTEXT_MENU_PT } from '@/shared/ui/RowContextMenu/contextMenuConfig';
import type { SyntheticEvent } from 'react';
import menuClasses from '@/shared/ui/RowContextMenu/RowContextMenu.module.scss';
import { getContextMenuEvent } from '@/shared/ui/RowContextMenu/contextMenuEvent';
import { ContextMenu } from 'primereact/contextmenu';
import {
    useLayoutEffect,
    useState,
    useRef,
    type MutableRefObject,
    type ReactNode,
} from 'react';
import { chatDialogFilterOptions } from '../config';
import type { ChatDialogListState } from '../types';
import { ChatChannelIcon } from '@/entities/ChatDialog/ui/ChatChannelIcon';
import { ApiChatMessageKind, type ChatDialogResponse } from '@/core/generated/api';
import { formatMoscowRelativeTimestamp } from '@/shared/lib/dateTime';
import classNames from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { useChatDialogWorkspace } from '@/entities/ChatDialog/model/workspace';
import { getChatDialogDisplayName, getChatDialogPreview } from '@/entities/ChatDialog/model/selectors';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestEmpty, RequestErrorBanner, RequestLoading } from '@/shared/ui/RequestState';
import classes from '../ChatDialogPage.module.scss';

interface ChatDialogListProps {
    state?: ChatDialogListState;
    filterControl?: ReactNode;

    /** Изменяет личное закрепление выбранной переписки */
    onPin?: (dialog: ChatDialogResponse) => Promise<void>;

    /** Блокирует повторное изменение закрепления */
    pinPending?: boolean;

    /** Меняет состояние чтения без открытия диалога */
    onReadState?: (dialog: ChatDialogResponse, unread: boolean) => Promise<void>;

    /** Проверяет право чтения в канале диалога */
    canChangeReadState?: (dialog: ChatDialogResponse) => boolean;

    /** Блокирует повторное изменение чтения */
    readPending?: boolean;

    /** Сохраняет положение общего списка при переключении каналов */
    scrollPosition?: MutableRefObject<number>;

    /** Открывает выбранную переписку */
    onSelect: (dialog: ChatDialogResponse) => void;

    /** Закрывает выбранную переписку */
    onClose: () => void;
}

/** Показывает все или непрочитанные диалоги */
export const ChatDialogList = (
    {
        onSelect, onClose, state, filterControl, scrollPosition, onPin, pinPending,
        onReadState, canChangeReadState, readPending,
    }: ChatDialogListProps
) => {
    const ownWorkspace = useChatDialogWorkspace(useShallow((state) => ({
        dialogs: state.dialogs,
        totalCount: state.totalCount,
        filter: state.filter,
        selectedDialogId: state.selectedDialog?.id,
        nextOffset: state.nextOffset,
        listStatus: state.listStatus,
        listError: state.listError,
        listMoreStatus: state.listMoreStatus,
        listMoreError: state.listMoreError,
        fetchDialogs: state.fetchDialogs,
        fetchMoreDialogs: state.fetchMoreDialogs,
        setFilter: state.setFilter,
    })));

    const workspace = state ?? ownWorkspace;
    const menu = useRef<ContextMenu>(null);
    const target = useRef<ChatDialogResponse | null>(null);
    const [pinnedMenu, setPinnedMenu] = useState(false);
    const [menuDialog, setMenuDialog] = useState<ChatDialogResponse | null>(null);
    const viewport = useRef<HTMLDivElement>(null);
    const ownPosition = useRef(0);
    const position = scrollPosition ?? ownPosition;
    const hasDialogs = workspace.dialogs.length > 0;

    useLayoutEffect(() => {
        if (viewport.current && hasDialogs) viewport.current.scrollTop = position.current;
    }, [position, hasDialogs, workspace.listStatus, workspace.selectedDialogId]);

    /** Открывает действия выбранного диалога */
    const showDialogMenu = (event: SyntheticEvent, dialog: ChatDialogResponse): void => {
        event.preventDefault();
        event.stopPropagation();

        if (pinPending || readPending) return;
        target.current = dialog;
        setMenuDialog(dialog);
        setPinnedMenu(Boolean(dialog.operatorPinnedAt));
        menu.current?.show(getContextMenuEvent(event));
    };

    /** Проверяет наличие действий для доступного канала */
    const hasActions = (dialog: ChatDialogResponse): boolean => Boolean(onPin
        || onReadState && (canChangeReadState?.(dialog) ?? true));

    const isLoadingMore = workspace.listMoreStatus === RequestStatus.Loading;
    const loadingMore = useRef(false);
    const lastScrollTop = useRef(0);

    /** Подгружает следующую страницу без параллельных запросов */
    const loadMoreDialogs = async (): Promise<void> => {
        const cannotLoadMore = loadingMore.current || isLoadingMore || workspace.nextOffset === null
            || workspace.listStatus !== RequestStatus.Success;

        if (cannotLoadMore) return;

        loadingMore.current = true;

        try {
            await workspace.fetchMoreDialogs();
        } finally {
            loadingMore.current = false;
        }
    };

    return (
        <aside
            className={classes.listPanel}
            aria-label="Список диалогов"
        >
            {(onPin || onReadState) && (
                <ContextMenu
                    pt={CONTEXT_MENU_PT}
                    className={menuClasses.contextMenu}
                    ref={menu}
                    ariaLabel="Действия с диалогом"
                    model={[...(onPin
                        ? [{
                            label: pinnedMenu
                                ? 'Открепить чат'
                                : 'Закрепить чат',
                            icon: 'pi pi-thumbtack',
                            disabled: pinPending,
                            command: () => {
                                const dialog = target.current;

                                if (dialog) void onPin(dialog);
                            },
                        }]
                        : []), ...(onReadState && menuDialog
                            && (canChangeReadState?.(menuDialog) ?? true)
                            ? [{
                                label: (menuDialog.unreadCount ?? 0) > 0
                                    ? 'Отметить прочитанным'
                                    : 'Отметить непрочитанным',
                                icon: (menuDialog.unreadCount ?? 0) > 0
                                    ? 'pi pi-envelope-open'
                                    : 'pi pi-envelope',
                                disabled: readPending,
                                command: () => {
                                    void onReadState(menuDialog, (menuDialog.unreadCount ?? 0) === 0);
                                },
                            }]
                            : []
                    )]}
                />
            )}
            <header className={classes.listHeader}>
                <span
                    className={classes.dialogCount}
                    aria-live="polite"
                >
                    Всего диалогов: {workspace.totalCount ?? '—'}
                </span>
                <div className={classes.filterBar}>
                    {filterControl}
                    <fieldset className={classes.filters}>
                        <legend>Показывать диалоги</legend>
                        {chatDialogFilterOptions.map((option) => (
                            <label
                                key={option.value}
                                title={option.title}
                            >
                                <input
                                    type="radio"
                                    name="chatDialogFilter"
                                    checked={workspace.filter === option.value}
                                    onChange={() => {
                                        position.current = 0;

                                        if (viewport.current) viewport.current.scrollTop = 0;
                                        onClose();
                                        workspace.setFilter(option.value);
                                    }}
                                />
                                <span>{option.label}</span>
                            </label>
                        ))}
                    </fieldset>
                </div>
            </header>

            <div
                ref={viewport}
                className={classes.listBody}
                onScroll={(event) => {
                    const element = event.currentTarget;
                    const movingDown = element.scrollTop > lastScrollTop.current;
                    lastScrollTop.current = element.scrollTop;

                    const shouldLoadMore = movingDown
                        && element.scrollHeight - element.scrollTop - element.clientHeight <= 160
                        && workspace.listMoreStatus !== RequestStatus.Error;

                    if (shouldLoadMore) {
                        void loadMoreDialogs();
                    }

                    if (hasDialogs && workspace.listStatus !== RequestStatus.Loading) {
                        position.current = event.currentTarget.scrollTop;
                    }
                }}
            >
                {workspace.listStatus === RequestStatus.Loading && !hasDialogs && (
                    <RequestLoading message="Загружаем диалоги…" />
                )}
                <RequestErrorBanner
                    status={workspace.listStatus}
                    error={workspace.listError}
                    fallbackMessage="Не удалось загрузить диалоги"
                    onRetry={workspace.fetchDialogs}
                />
                {workspace.listStatus === RequestStatus.Success && workspace.dialogs.length === 0 && (
                    <RequestEmpty message={workspace.filter === 'unread'
                        ? 'Непрочитанных диалогов нет'
                        : 'Диалогов пока нет'
                    } />
                )}
                {(workspace.listStatus === RequestStatus.Success || hasDialogs) && (
                    <ul className={classes.dialogs}>
                        {workspace.dialogs.map((dialog) => {
                            const name = getChatDialogDisplayName(dialog);
                            const unreadCount = dialog.unreadCount ?? 0;
                            const selected = dialog.id === workspace.selectedDialogId;

                            return (
                                <li key={dialog.id}>
                                    <button
                                        type="button"
                                        title={name}
                                        aria-label={name}
                                        className={classNames(classes.dialogItem, { [classes.selectedDialog]: selected })}
                                        aria-current={selected
                                            ? 'true'
                                            : undefined
                                        }
                                        onClick={() => onSelect(dialog)}
                                        onContextMenu={hasActions(dialog)
                                            ? (event) => showDialogMenu(event, dialog)
                                            : undefined
                                        }
                                    >
                                        <span className={classes.avatarWrapper}>
                                            <span
                                                className={classes.avatar}
                                                aria-hidden="true"
                                            >
                                                {dialog.companionAvatarUrl
                                                    ? (
                                                        <img
                                                            src={dialog.companionAvatarUrl}
                                                            alt=""
                                                            loading="lazy"
                                                        />
                                                    )
                                                    : name.charAt(0).toUpperCase()}
                                            </span>
                                            {unreadCount > 0 && (
                                                <span
                                                    className={classes.unreadBadge}
                                                    aria-label={`Непрочитанных сообщений: ${unreadCount}`}
                                                >
                                                    {unreadCount > 99
                                                        ? '99+'
                                                        : unreadCount}
                                                </span>
                                            )}
                                        </span>
                                        <span className={classes.dialogContent}>
                                            <span className={classes.dialogHeading}>
                                                <strong title={name}>{name}</strong>
                                                <time dateTime={dialog.lastActivityAt ?? undefined}>
                                                    {formatMoscowRelativeTimestamp(dialog.lastActivityAt)}
                                                </time>
                                            </span>
                                            <span className={classes.preview}>
                                                {dialog.lastMessage?.isOutgoing
                                                    && dialog.lastMessage.kind?.id !== ApiChatMessageKind.System && (
                                                        <i
                                                            className={classNames('pi pi-arrow-up-right', classes.outgoingIndicator)}
                                                            role="img"
                                                            aria-label="Исходящее сообщение"
                                                            title="Исходящее сообщение"
                                                        />
                                                    )}
                                                <span className={classes.previewText}>{getChatDialogPreview(dialog.lastMessage)}</span>
                                            </span>
                                            <span className={classes.dialogMetadata}>
                                                {dialog.operatorPinnedAt && (
                                                    <i
                                                        className="pi pi-thumbtack"
                                                        aria-label="Закреплённый диалог"
                                                    />
                                                )}
                                                <ChatChannelIcon channel={dialog.channel} />
                                            </span>
                                        </span>
                                    </button>
                                    {hasActions(dialog) &&
                                        <button
                                            type="button"
                                            className={classes.dialogMenuButton}
                                            aria-label="Действия с диалогом"
                                            aria-haspopup="menu"
                                            disabled={pinPending || readPending}
                                            onClick={event => showDialogMenu(event, dialog)}
                                        >
                                            <i
                                                className="pi pi-ellipsis-h"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    }
                                </li>
                            );
                        })}
                    </ul>
                )}
                <RequestErrorBanner
                    status={workspace.listMoreStatus}
                    error={workspace.listMoreError}
                    fallbackMessage="Не удалось загрузить ещё диалоги"
                    onRetry={loadMoreDialogs}
                />
                {workspace.listStatus === RequestStatus.Success && workspace.nextOffset !== null && (
                    <div className={classes.pagination}>
                        <ActionButton
                            type="button"
                            label="Ещё диалоги"
                            color="secondary"
                            loading={isLoadingMore}
                            disabled={isLoadingMore}
                            onClick={loadMoreDialogs}
                        />
                    </div>
                )}
            </div>
        </aside>
    );
};
